import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { verifyMasterKey } from '../lib/crypto';

interface MasterKeyContextType {
  getMasterKey: (companyId: string) => string | null;
  setMasterKey: (companyId: string, masterKey: string) => void;
  clearMasterKey: (companyId: string) => void;
  clearAllMasterKeys: () => void;
  hasMasterKey: (companyId: string) => boolean;
  verifyAndSetMasterKey: (companyId: string, masterKey: string, masterKeyHash: string) => Promise<boolean>;
}

const MasterKeyContext = createContext<MasterKeyContextType | undefined>(undefined);

export function MasterKeyProvider({ children }: { children: ReactNode }) {
  const [masterKeys, setMasterKeys] = useState<Record<string, string>>({});

  const getMasterKey = useCallback((companyId: string): string | null => {
    return masterKeys[companyId] || null;
  }, [masterKeys]);

  const setMasterKeyFn = useCallback((companyId: string, masterKey: string) => {
    setMasterKeys(prev => ({
      ...prev,
      [companyId]: masterKey
    }));
  }, []);

  const clearMasterKey = useCallback((companyId: string) => {
    setMasterKeys(prev => {
      const newKeys = { ...prev };
      delete newKeys[companyId];
      return newKeys;
    });
  }, []);

  const clearAllMasterKeys = useCallback(() => {
    setMasterKeys({});
  }, []);

  const hasMasterKey = useCallback((companyId: string): boolean => {
    return !!masterKeys[companyId];
  }, [masterKeys]);

  const verifyAndSetMasterKey = useCallback(async (
    companyId: string,
    masterKey: string,
    masterKeyHash: string
  ): Promise<boolean> => {
    try {
      const isValid = await verifyMasterKey(masterKey, masterKeyHash);
      if (isValid) {
        setMasterKeys(prev => ({
          ...prev,
          [companyId]: masterKey
        }));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  return (
    <MasterKeyContext.Provider
      value={{
        getMasterKey,
        setMasterKey: setMasterKeyFn,
        clearMasterKey,
        clearAllMasterKeys,
        hasMasterKey,
        verifyAndSetMasterKey
      }}
    >
      {children}
    </MasterKeyContext.Provider>
  );
}

export function useMasterKey() {
  const context = useContext(MasterKeyContext);
  if (context === undefined) {
    throw new Error('useMasterKey must be used within a MasterKeyProvider');
  }
  return context;
}
